/**
 * F44 (Wave 2D) — shared finance dashboard helpers (date-range presets + a
 * couple of small formatters). Periods are ISO `YYYY-MM-DD` in Asia/Beirut
 * (the only MVP locale); the range presets are computed against the local
 * `now` for the dev/admin browser — Lebanon-only at MVP, so this is correct.
 */

export type FinanceQuickPreset =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'ytd'
  | 'custom';

export type PercentDeltaTone = 'positive' | 'negative' | 'neutral';

/** Resolve the Stat tone for a signed delta (positive=success, negative=danger). */
export function deltaTone(deltaPercent: number): PercentDeltaTone {
  if (deltaPercent > 0) return 'positive';
  if (deltaPercent < 0) return 'negative';
  return 'neutral';
}

/**
 * Round a percent value to 1 decimal for display. The backend emits raw float
 * deltas/rates (e.g. 16.666666666666664) — rounding happens at render time
 * (the percent-values-unrounded audit fix).
 */
export function roundPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

export interface FinanceRangeValue {
  readonly from: string;
  readonly to: string;
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Resolve a quick-preset to a concrete `{from,to}` ISO range. `custom` returns null. */
export function resolvePreset(
  preset: FinanceQuickPreset,
  now = new Date(),
): FinanceRangeValue | null {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'today':
      return { from: iso(now), to: iso(now) };
    case 'thisWeek': {
      // ISO-ish week: Monday → today.
      const day = now.getDay();
      const diffToMonday = (day + 6) % 7;
      const monday = new Date(y, m, now.getDate() - diffToMonday);
      return { from: iso(monday), to: iso(now) };
    }
    case 'thisMonth':
      return { from: iso(new Date(y, m, 1)), to: iso(now) };
    case 'lastMonth': {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0); // last day of previous month
      return { from: iso(start), to: iso(end) };
    }
    case 'ytd':
      return { from: iso(new Date(y, 0, 1)), to: iso(now) };
    case 'custom':
      return null;
  }
}

/** Default landing range = this month. */
export function defaultFinanceRange(now = new Date()): FinanceRangeValue {
  return resolvePreset('thisMonth', now)!;
}

/** Format an ISO timestamp as a short `HH:MM` local time for the refresh banner. */
export function formatRefreshTime(isoTimestamp: string | null, locale = 'en-US'): string | null {
  if (!isoTimestamp) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Beirut',
    }).format(new Date(isoTimestamp));
  } catch {
    return isoTimestamp;
  }
}

/** Format an ISO timestamp/date as a short readable date. */
export function formatShortDate(isoTimestamp: string | null, locale = 'en-US'): string {
  if (!isoTimestamp) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(isoTimestamp));
  } catch {
    return isoTimestamp;
  }
}

/** Format an ISO date as a "Mon YYYY" period label. */
export function formatPeriodMonth(isoTimestamp: string, locale = 'en-US'): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'Asia/Beirut',
    }).format(new Date(isoTimestamp));
  } catch {
    return isoTimestamp;
  }
}
